import React, { useEffect, useState } from 'react';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

/**
 * Toggle that controls 1st-of-month billing for new subscription products
 * and for migrating existing subscriptions in the bulk upgrade flow.
 * Persisted on profiles.anchor_billing_enabled.
 */
const AnchorBillingSettings: React.FC = () => {
  const [enabled, setEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchSetting = async () => {
      setIsLoading(true);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('anchor_billing_enabled')
          .eq('id', user.id)
          .single();

        setEnabled(Boolean(profile?.anchor_billing_enabled));
      } catch (err) {
        console.error('[AnchorBillingSettings] Error fetching setting:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSetting();
  }, []);

  const handleToggleChange = async (checked: boolean) => {
    setEnabled(checked);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('profiles')
        .update({ anchor_billing_enabled: checked, updated_at: new Date().toISOString() })
        .eq('id', user.id);

      if (error) throw error;

      toast({
        title: 'Setting updated',
        description: checked
          ? 'New subscriptions will bill on the 1st of each month, and existing ones will migrate when you send the payment update email.'
          : 'Anchor billing is now disabled.',
        duration: 3000,
      });
    } catch (err) {
      console.error('[AnchorBillingSettings] Error saving setting:', err);
      toast({
        title: 'Error saving setting',
        description: 'There was a problem updating the anchor billing setting. Please try again.',
        variant: 'destructive',
        duration: 5000,
      });
      setEnabled(!checked);
    }
  };

  return (
    <Card className="bg-gray-50 border-gray-200">
      <CardHeader>
        <CardTitle>Anchor Billing</CardTitle>
        <CardDescription>
          Align new and existing subscriptions to a fixed monthly billing date
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div className="space-y-0.5 pr-4">
            <Label htmlFor="anchor-billing-enabled">Bill on the 1st of each month</Label>
            <p className="text-sm text-muted-foreground">
              When enabled, new subscription products bill on the 1st of each month.
              Sending the payment update email also moves selected existing
              subscriptions to that same cycle.
            </p>
          </div>
          <Switch
            id="anchor-billing-enabled"
            checked={enabled}
            onCheckedChange={handleToggleChange}
            disabled={isLoading}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default AnchorBillingSettings;
